import { api } from "../scripts/api.js";

/*
   <app-pack-rules> - one timed pack in one class: its rules and its best-time board.

   Route: #pack-rules/<classId>/<packId>         the rules
          #pack-rules/<classId>/<packId>/board   the best-time board
          #pack-rules/<classId>/<packId>/exam    EXAM MODE (2026-10-16): Open, Release wave 2,
                                                 End, and the live board (EXAM-MODE-DESIGN-2026-10-15.md)
   Reached from the pack's card on the class page (class-detail.js). Built
   2026-10-14 (Session AL). Design: second brain,
   Timed-Tab-Build-DESIGN-2026-10-13.md, decision 11 (rules are data) and
   David's rulings of 2026-10-14.

   SERVER, NOT THE PAGE, IS THE AUTHORITY. Reads GET /classes/:c/packs/:p/rules
   (pack rules, this class's override, the merged result, the defaults, the tabs)
   and writes PUT the same path with { override }. lib/pack-rules.js validates
   every save; the page only sorts the rows and catches the obvious slips first,
   so a teacher hears about them before the round trip. Anything the server
   refuses is shown as it said it.

   THE OVERRIDE IS KEY BY KEY. The server merges the class's override over the
   pack's rules one key at a time, so a threshold LIST is replaced whole, never
   merged row by row. That is why each setting has its own "Set for this class"
   switch: off means the key is absent from the override and the pack's own value
   applies, including if the pack's rules are ever re-seeded.

   WHAT A SAVE CHANGES, AND WHEN (said on the page, because it surprises people):
     grade rows       at once, for every run already finished. The grade is worked
                      out from the runs each time it is read (derived on read).
     everything else  runs started after the save. A run keeps a copy of the
                      rules it started with, so nobody's clock or penalty changes
                      mid-run.

   THE BOARD. GET /packs/:p/board?class_id=c. A teacher gets the real name and
   email beside the nickname classmates see, plus the derived grade. Students see
   the same board on Class standings (class-boards.js), nickname only and no
   grade, and only when the class has "Class scoreboard" on.
*/

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

// 175 -> "2:55", 175.4 -> "2:55.4" (tenths only when there are any)
function fmtClock(s) {
   if (s == null || !isFinite(Number(s))) return "-";
   const n = Number(s);
   const m = Math.floor(n / 60);
   const rest = n - m * 60;
   const whole = Math.abs(rest - Math.round(rest)) < 0.05;
   const sec = whole ? String(Math.round(rest)).padStart(2, "0") : rest.toFixed(1).padStart(4, "0");
   return `${m}:${sec}`;
}

// "2:55" | "175" | "2:55.5" -> seconds; null when it is not a time
function parseClock(text) {
   const t = String(text ?? "").trim();
   if (!t) return null;
   const m = /^(\d+):([0-5]?\d(?:\.\d+)?)$/.exec(t);
   if (m) return Number(m[1]) * 60 + Number(m[2]);
   if (/^\d+(\.\d+)?$/.test(t)) return Number(t);
   return NaN;
}

const pct = (g) => `${Math.round(Number(g) * 10000) / 100}%`;

const DEPARTURE = {
   penalty: { label: "Add penalty seconds", help: "Each time the student leaves the workbook (another workbook, another app, another browser tab), the penalty is added to that run's time." },
   restart: { label: "Restart the run", help: "Leaving the workbook ends the run and starts a fresh one with new numbers. No penalty; the abandoned run does not count." },
   ignore: { label: "Ignore", help: "Leaving is recorded but changes nothing." },
};

/*
   One entry per rule key, in the order a teacher thinks about them. `kind`
   picks the editor. Every key in lib/pack-rules.js DEFAULT_RULES appears here,
   so nothing a class can override is hidden from the page.
*/
const FIELDS = [
   { key: "time_thresholds", kind: "time-rows", label: "Grade by best time",
     help: "The student's fastest completed timed run, penalties included. The first row the time fits gives the grade." },
   { key: "attempt_thresholds", kind: "attempt-rows", label: "Grade by completed runs",
     help: "How many timed runs the student has finished, penalties or not. Stopped, expired and restarted runs do not count." },
   { key: "departure", kind: "departure", label: "Leaving the workbook",
     help: "What happens when a student switches away during a timed run." },
   { key: "penalty_secs", kind: "secs", label: "Penalty per departure",
     help: "Seconds added each time, when the setting above is \"Add penalty seconds\"." },
   { key: "extra_check", kind: "bool", label: "Extra check",
     help: "Before a tab clears, the add-in tries a second set of inputs. Answers typed in as numbers fail it; formulas pass." },
   { key: "practice_allowed", kind: "bool", label: "Practice runs",
     help: "Every tab at once, no clock, no extra check, never counted toward the grade." },
   { key: "max_secs", kind: "minutes", label: "Time limit per run",
     help: "A run that reaches this ends unfinished and does not count." },
   { key: "approx_answers", kind: "bool", label: "Approximate answers",
     help: "Show the rounded answer beside each answer cell, as the Builder files did." },
   // EXAM MODE (2026-10-16)
   { key: "mode", kind: "mode", label: "Kind",
     help: "Timed pack: one tab at a time, graded by time. Exam: you start it for the whole class, tabs arrive in waves, graded by points." },
   { key: "waves", kind: "waves", label: "Exam waves",
     help: "How many tabs arrive at the start and how many later, in tab order, e.g. 3, 3. Must add up to the number of tabs." },
   { key: "wave2_after_secs", kind: "opt-minutes", label: "Wave 2 arrives after",
     help: "Minutes after you open the exam. Leave blank to release wave 2 only with the button." },
   { key: "wave2_on_clear", kind: "bool", label: "Wave 2 early for a perfect wave 1",
     help: "On: a student whose wave-1 answers are ALL correct gets wave 2 right away, without waiting for the clock or the button. Set it before you open the exam." },
   { key: "self_start", kind: "bool", label: "Students start it themselves (practice)",
     help: "On: no Open exam. Each student presses Start for their own clock, with the same waves; they can try as often as they like; never graded and no workbook collection. The Exam tab shows each student's tries and best score." },
   { key: "collect_file", kind: "bool", label: "Collect students' workbooks",
     help: "On: the add-in sends each student's workbook to you when the exam ends (students can also press Submit my file); download them from the Exam board. The Start Here sheet tells students." },
   { key: "ai_flag_student_popup", kind: "bool", label: "Show AI flags to students",
     help: "Off: only you see suspected AI building on the live board. On: the student also gets a warning in the add-in (use for a demo)." },
   // AI FINGERPRINTS (2026-10-17, server lib/ai-flags.js). "New formula" = a formula that is not a
   // copy of one already in the workbook: filling or pasting your own work never counts.
   { key: "ai_detect", kind: "bool", label: "AI flags",
     help: "Watch exam runs for AI building and flag it on the live board. AI may be used as a reference, never to build." },
   { key: "ai_block_cells", kind: "int", unit: "new formulas", min: 1, max: 500, label: "AI flag: written at once",
     help: "This many new formulas written within 2 seconds (in one change or several) is flagged. A person types one formula at a time." },
   { key: "ai_burst_cells", kind: "int", unit: "new formulas", min: 1, max: 500, label: "AI flag: a burst of",
     help: "This many new formulas inside the burst window is flagged, however they arrived." },
   { key: "ai_burst_secs", kind: "int", unit: "seconds", min: 1, max: 600, label: "AI flag: burst window",
     help: "The window for the burst and for writing away from the cursor." },
   { key: "ai_agent_cells", kind: "int", unit: "new formulas", min: 1, max: 500, label: "AI flag: written away from the cursor",
     help: "This many new formulas (inside the burst window) in cells the student had not selected, while they were not working in the grid, is flagged." },
   { key: "ai_idle_secs", kind: "int", unit: "seconds", min: 0, max: 600, label: "AI flag: not working in the grid for",
     help: "How long the student's selection must have sat still for a write away from the cursor to count." },
];

const AI_REASON = {
   block: "many new formulas at once",
   burst: "too many new formulas too fast",
   agent_write: "written away from the cursor while the student was not in the grid",
};

function describe(field, v) {
   if (v === undefined) return "-";
   switch (field.kind) {
      case "time-rows":
         if (!v.length) return "No time grades";
         return v.map(([t, g]) => t === null ? `Any finish: ${pct(g)}` : `${fmtClock(t)} or faster: ${pct(g)}`).join("<br>");
      case "attempt-rows":
         if (!v.length) return "No attempt grades";
         return v.map(([n, g]) => `${n === 1 ? "1 run" : `${n} or more runs`}: ${pct(g)}`).join("<br>");
      case "departure": return esc(DEPARTURE[v]?.label || v);
      case "secs": return `${v} s${v >= 60 ? ` (${fmtClock(v)})` : ""}`;
      case "minutes": return `${Math.round(v / 6) / 10} min`;
      case "bool": return v ? "On" : "Off";
      case "mode": return v === "exam" ? "Exam" : "Timed pack";
      case "waves": return Array.isArray(v) && v.length ? esc(v.join(", ")) : "All tabs at once";
      case "opt-minutes": return v == null ? "Button only" : `${Math.round(v / 6) / 10} min`;
      case "int": return `${esc(v)} ${esc(field.unit || "")}`;
      default: return esc(JSON.stringify(v));
   }
}

// Order-blind comparison of two overrides (the server may hand keys back in any order).
const canon = (o) => JSON.stringify(Object.keys(o || {}).sort().map((k) => [k, o[k]]));
const same = (a, b) => canon(a) === canon(b);

class AppPackRules extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="pack-rules">
            <div class="page-head">
               <button type="button" id="pr-back">&larr; Class</button>
               <h1 id="pr-title">Loading...</h1>
               <p class="sub" id="pr-sub"></p>
            </div>

            <div class="pr-tabs" role="group" aria-label="View">
               <a class="pr-tab" data-tab="rules">Rules</a>
               <a class="pr-tab" data-tab="board">Best times</a>
               <a class="pr-tab" data-tab="exam">Exam</a>
               <a class="pr-tab" data-tab="units">Tabs and solutions</a>
            </div>

            <div id="pr-rules-view">
               <div class="pr-note">
                  <strong>How the grade works.</strong> A student's grade on this pack is the
                  higher of their time grade and their completed-runs grade. It is worked out
                  from their runs every time it is shown, so <strong>changing a grade row
                  changes grades already earned</strong>, for everyone in this class, gradebook
                  included. The other
                  settings apply to runs started after you save; a run in progress keeps the
                  rules it started with.
               </div>
               <p class="msg" id="pr-msg"></p>
               <div id="pr-form"></div>
               <div class="pr-actions" id="pr-actions" hidden>
                  <button type="button" id="pr-save">Save for this class</button>
                  <button type="button" class="ghost" id="pr-reset">Use the pack's rules for everything</button>
                  <span class="msg" id="pr-save-msg"></span>
               </div>
            </div>

            <div id="pr-board-view" hidden>
               <p class="msg" id="pr-board-msg"></p>
               <div id="pr-board"></div>
            </div>

            <div id="pr-units-view" hidden><app-pack-units embedded></app-pack-units></div>

            <div id="pr-exam-view" hidden>
               <div class="ex-controls">
                  <button type="button" id="ex-open">Open exam</button>
                  <button type="button" id="ex-release">Release wave 2 now</button>
                  <button type="button" class="ghost" id="ex-close">End exam now</button>
                  <label class="ex-proj"><input type="checkbox" id="ex-projector"> Projector view (nicknames, no flags)</label>
               </div>
               <p class="msg" id="pr-exam-msg"></p>
               <div id="ex-status"></div>
               <div id="ex-board"></div>
               <div id="ex-ai-bar" class="ex-ai-bar"></div>
               <div id="ex-ai" class="ex-ai" hidden></div>
            </div>
         </section>`;

      this.querySelector("#pr-back").addEventListener("click", () => {
         location.hash = this.classId ? `#class-detail/${this.classId}` : "#classes";
      });
      this.querySelector("#pr-save").addEventListener("click", () => this.save());
      this.querySelector("#ex-open").addEventListener("click", () => this.examAct("open"));
      this.querySelector("#ex-release").addEventListener("click", () => this.examAct("release"));
      this.querySelector("#ex-close").addEventListener("click", () => this.examAct("close"));
      this.querySelector("#ex-projector").addEventListener("change", () => this.renderExam());
      // AI fingerprints (2026-10-17): a flag chip opens that student's timeline; the bar re-checks with the current rules.
      this.querySelector("#ex-board").addEventListener("click", (e) => {
         const b = e.target.closest("[data-ai-user]");
         if (b) this.loadAi(Number(b.dataset.aiUser));
      });
      this.querySelector("#ex-ai-bar").addEventListener("click", (e) => {
         if (e.target.closest("#ex-ai-rescan")) this.rescanAi();
      });
      this.querySelector("#ex-ai").addEventListener("click", (e) => {
         if (e.target.closest("#ex-ai-close")) { this.aiUser = null; this.querySelector("#ex-ai").hidden = true; }
         if (e.target.closest("#ex-ai-refresh") && this.aiUser != null) this.loadAi(this.aiUser);
      });
      this.querySelector("#pr-reset").addEventListener("click", () => this.reset());
   }

   /* param = "<classId>/<packId>" or "<classId>/<packId>/board" */
   async show(param) {
      const [c, p, sub] = String(param || "").split("/");
      const classId = Number(c), packId = Number(p);
      if (!Number.isInteger(classId) || !Number.isInteger(packId)) {
         this.querySelector("#pr-title").textContent = "No pack selected.";
         return;
      }
      const tab = sub === "board" ? "board" : sub === "exam" ? "exam" : sub === "units" ? "units" : "rules";
      const fresh = classId !== this.classId || packId !== this.packId;
      this.classId = classId;
      this.packId = packId;
      if (fresh) this.view = null;
      this.querySelectorAll(".pr-tab").forEach((a) => {
         a.href = `#pack-rules/${classId}/${packId}${a.dataset.tab === "rules" ? "" : "/" + a.dataset.tab}`;
         a.classList.toggle("on", a.dataset.tab === tab);
      });
      this.querySelector("#pr-rules-view").hidden = tab !== "rules";
      this.querySelector("#pr-board-view").hidden = tab !== "board";
      this.querySelector("#pr-exam-view").hidden = tab !== "exam";
      this.querySelector("#pr-units-view").hidden = tab !== "units";
      this.stopExamPoll();
      if (fresh) await this.loadHeader();
      if (tab === "rules") await this.loadRules(!fresh);
      else if (tab === "board") await this.loadBoard();
      else if (tab === "units") {
         // Tabs and solutions (components/pack-units.js), as a tab of the pack page (David 2026-10-16)
         const pu = this.querySelector("app-pack-units");
         if (pu && typeof pu.show === "function" && (fresh || pu.classId !== classId || pu.packId !== packId)) await pu.show(`${classId}/${packId}`);
      }
      else await this.loadExam(true);
   }

   disconnectedCallback() { this.stopExamPoll(); }

   /* ------------------------------------------------------------------ exam (2026-10-16) */

   stopExamPoll() {
      if (this.examTimer) { clearInterval(this.examTimer); this.examTimer = null; }
      if (this.examTick) { clearInterval(this.examTick); this.examTick = null; }
   }

   /* The board refreshes every 3 s while this view is on screen; the clock ticks every second between. */
   async loadExam(first = false) {
      const msg = this.querySelector("#pr-exam-msg");
      const want = { classId: this.classId, packId: this.packId };
      if (first) {
         this.exam = null;
         this.querySelector("#ex-board").innerHTML = `<p class="empty">Loading...</p>`;
      }
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/packs/${encodeURIComponent(this.packId)}/exam`);
         const data = await res.json();
         if (this.classId !== want.classId || this.packId !== want.packId) return;
         if (!res.ok || !data.ok) throw new Error(data.reason || String(res.status));
         this.exam = data;
         if (msg.dataset.loadErr) { msg.textContent = ""; msg.className = "msg"; delete msg.dataset.loadErr; }   // a LOAD error clears once the board loads again (10/17)
         else if (!first) msg.textContent = msg.className.includes("err") ? msg.textContent : "";
         this.renderExam();
         if (!this.examTimer && !this.querySelector("#pr-exam-view").hidden) {
            this.examTimer = setInterval(() => {
               if (this.querySelector("#pr-exam-view").hidden || !this.isConnected) { this.stopExamPoll(); return; }
               this.loadExam(false);
            }, 3000);
            this.examTick = setInterval(() => {
               const s = this.exam && this.exam.session;
               if (s && s.state === "open" && s.remaining_secs > 0) {
                  s.remaining_secs -= 1; s.elapsed_secs += 1;
                  if (s.base_remaining_secs > 0) s.base_remaining_secs -= 1;
                  this.renderStatus();
               }
            }, 1000);
         }
      } catch (err) {
         if (this.classId !== want.classId || this.packId !== want.packId) return;
         const why = String(err.message);
         this.querySelector("#ex-board").innerHTML = "";
         this.querySelector("#ex-status").innerHTML = "";
         msg.textContent = why === "not_an_exam"
            ? "This pack is not set up as an exam. On the Rules tab, set Kind to Exam (and the waves) for this class."
            : why === "not_authorized" || why === "403" ? "Only the class's instructor can run the exam." : "Couldn't load the exam.";
         msg.className = "msg err";
         msg.dataset.loadErr = "1";
         this.stopExamPoll();
      }
   }

   async examAct(act) {
      const msg = this.querySelector("#pr-exam-msg");
      const btns = this.querySelectorAll(".ex-controls button");
      btns.forEach((b) => (b.disabled = true));
      msg.textContent = act === "open" ? "Opening..." : act === "release" ? "Releasing wave 2..." : "Ending the exam...";
      msg.className = "msg";
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/packs/${encodeURIComponent(this.packId)}/exam/${act}`, {
            method: "POST", body: "{}" });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.detail || data.reason || String(res.status));
         msg.textContent = act === "open" ? (data.already_open ? "The exam was already open." : "The exam is open. Students' tabs arrive within a few seconds.")
            : act === "release" ? "Wave 2 released. It reaches students on their next poll (about 3 seconds)."
            : `Exam ended. ${data.finalized || 0} run${data.finalized === 1 ? "" : "s"} graded.`;
         msg.className = "msg ok";
         await this.loadExam(false);
      } catch (err) {
         const why = String(err.message);
         msg.textContent = why === "no_open_exam" ? "There is no open exam."
            : why === "self_start_pack" ? "Students start this one themselves (Rules tab: Students start it themselves)." : `Didn't work (${why}).`;
         msg.className = "msg err";
      } finally {
         btns.forEach((b) => (b.disabled = false));
         this.renderStatus();
      }
   }

   renderStatus() {
      const el = this.querySelector("#ex-status");
      if (!el || !this.exam) return;
      if (this.exam.self_start) {
         // SELF-START practice (2026-10-19): nothing for the instructor to open, release or end.
         ["#ex-open", "#ex-release", "#ex-close"].forEach((id) => { const b = this.querySelector(id); if (b) b.disabled = true; });
         const r = this.exam.rules || {};
         const w = Array.isArray(r.waves) && r.waves.length > 1 ? ` Tabs arrive in waves of ${esc(r.waves.join(" + "))}${r.wave2_after_secs != null ? `, the second at ${fmtClock(r.wave2_after_secs)}` : ""}${r.wave2_on_clear ? " or as soon as the first wave is all correct" : ""}.` : "";
         el.innerHTML = `<p class="ex-state"><strong>Self-start practice.</strong> Each student presses Start for their own ${fmtClock(r.max_secs || 0)} clock and can try as often as they like.${w} Never graded. To run it as a real exam instead, turn off "Students start it themselves" on the Rules tab.</p>`;
         return;
      }
      const s = this.exam.session;
      const open = s && s.state === "open";
      this.querySelector("#ex-open").disabled = !!open;
      this.querySelector("#ex-release").disabled = !open || s.released >= s.stage_count;
      this.querySelector("#ex-close").disabled = !open;
      if (!s) { el.innerHTML = `<p class="ex-state">Not started. Students who open the exam file see "Waiting for your instructor".</p>`; return; }
      const waves = (s.waves || []).join(" + ");
      const auto = s.wave2_after_secs != null && s.released < s.stage_count
         ? ` Wave 2 releases by itself at ${fmtClock(s.wave2_after_secs)} (in ${fmtClock(Math.max(0, s.wave2_after_secs - s.elapsed_secs))}).` : "";
      const early = s.wave2_on_clear && s.released < s.stage_count
         ? " A student whose wave-1 answers are all correct gets wave 2 right away." : "";
      // Extra time (2026-10-16): the big clock is the REGULAR buzzer; extended runs keep the session open longer.
      const base = s.base_remaining_secs != null ? s.base_remaining_secs : s.remaining_secs;
      const ext = s.remaining_secs > base + 5
         ? ` Students with extra time keep going until ${fmtClock(s.remaining_secs)} from now; the exam stays open for them.` : "";
      el.innerHTML = open
         ? `<div class="ex-clock">${fmtClock(base)}</div>
            <p class="ex-state">Open. ${s.released} of ${s.stage_count} tabs released (waves ${esc(waves)}).${auto}${early}${ext}</p>`
         : `<p class="ex-state">Ended ${s.ends_at ? new Date(s.ends_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}. Scores are final; the grades are in the gradebook.</p>`;
   }

   /* ------------------------------------------------------------------ AI fingerprints (2026-10-17) */
   renderAiBar(proj) {
      const bar = this.querySelector("#ex-ai-bar");
      const ai = this.exam && this.exam.ai;
      if (!bar) return;
      if (proj || !ai) { bar.innerHTML = ""; if (proj) this.querySelector("#ex-ai").hidden = true; return; }
      if (this.exam.self_start) {   // practice: flags are counted per student across their tries; nothing to re-check here
         bar.innerHTML = ai.detect ? `<span><strong>AI flags on.</strong> The AI flags column adds up each student's flags across their practice tries. Practice is never graded.</span>` : "";
         return;
      }
      const t = ai.thresholds || {};
      bar.innerHTML = ai.detect
         ? `<span><strong>AI flags on.</strong> Flagged: ${esc(t.block_cells)}+ new formulas within 2 s, ${esc(t.burst_cells)}+ in ${esc(t.burst_secs)} s,
              or ${esc(t.agent_cells)}+ written away from the cursor after ${esc(t.idle_secs)} s out of the grid. Copies of the student's own work never count.
              Student pop-up ${ai.popup ? "<strong>ON</strong>" : "off"}. A flag is evidence for a conversation, not a verdict.</span>
            <button type="button" class="ghost" id="ex-ai-rescan">Re-check with the current rules</button>
            ${this.aiNote ? `<span class="ex-ai-note">${esc(this.aiNote)}</span>` : ""}`
         : `<span>AI flags are off for this exam (Rules tab).</span>`;
   }

   async rescanAi() {
      const btn = this.querySelector("#ex-ai-rescan");
      if (btn) { btn.disabled = true; btn.textContent = "Re-checking..."; }
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/packs/${encodeURIComponent(this.packId)}/exam/ai/rescan`, { method: "POST" });
         const out = await res.json().catch(() => ({}));
         // Say what happened (10/17: a silent button looked broken). The route re-checks the LATEST exam session.
         const at = new Date().toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
         this.aiNote = out.ok
            ? `Re-checked at ${at}: exam session ${out.session_id} (the latest), ${out.runs} student run${out.runs === 1 ? "" : "s"}, ${out.flags} flag${out.flags === 1 ? "" : "s"} under the current rules.`
            : `Re-check failed at ${at} (${out.reason || `error ${res.status}`}).`;
         await this.loadExam(false);
         if (this.aiUser != null) await this.loadAi(this.aiUser);
      } finally {
         const b = this.querySelector("#ex-ai-rescan");
         if (b) { b.disabled = false; b.textContent = "Re-check with the current rules"; }
      }
   }

   async loadAi(userId) {
      const panel = this.querySelector("#ex-ai");
      this.aiUser = userId;
      panel.hidden = false;
      const who = this.exam && this.exam.students ? this.exam.students.find((s) => Number(s.user_id) === Number(userId)) : null;
      panel.innerHTML = `<p class="msg">Loading ${esc(who ? who.name : "the student")}'s timeline...</p>`;
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/packs/${encodeURIComponent(this.packId)}/exam/ai?user_id=${encodeURIComponent(userId)}`);
         const d = await res.json();
         if (!res.ok || !d.ok) throw new Error(d.reason || String(res.status));
         panel.innerHTML = this.aiHtml(d, who);
      } catch (err) {
         panel.innerHTML = `<p class="msg err">Couldn't load the timeline (${esc(err.message)}).</p>
            <button type="button" class="ghost" id="ex-ai-close">Close</button>`;
      }
   }

   aiHtml(d, who) {
      const mmss = (s) => fmtClock(Math.max(0, Math.round(s)));
      const series = d.series || [], flags = d.flags || [];
      const end = Math.max(60, ...series.map((x) => x.secs), ...flags.map((f) => f.last_secs || 0)) * 1.02;
      const W = 1000, H = 70, X = (s) => Math.round((Math.max(0, s) / end) * W * 10) / 10;
      const maxCells = Math.max(4, ...series.map((x) => x.novel + x.copied));
      // Square-root heights: a 40-cell paste must not flatten the one-formula-at-a-time typing beside it.
      const Y = (n) => Math.max(3, Math.round(Math.sqrt(n / maxCells) * (H - 8)));
      const bands = flags.map((f) => `<rect x="${X(f.first_secs)}" y="0" width="${Math.max(4, X(f.last_secs) - X(f.first_secs))}" height="${H}" class="ex-ai-band"><title>Flag at ${mmss(f.first_secs)}</title></rect>`).join("");
      const bars = series.map((x) => {
         const c = x.copied ? `<rect x="${X(x.secs)}" y="${H - Y(x.copied + x.novel)}" width="3" height="${Y(x.copied)}" class="ex-ai-copy"><title>${mmss(x.secs)} ${esc(x.address)}: ${x.copied} copied</title></rect>` : "";
         const n = x.novel ? `<rect x="${X(x.secs)}" y="${H - Y(x.novel)}" width="3" height="${Y(x.novel)}" class="${x.off_cursor ? "ex-ai-off" : "ex-ai-new"}"><title>${mmss(x.secs)} ${esc(x.address)}: ${x.novel} new${x.off_cursor ? ", away from the cursor" : ""}</title></rect>` : "";
         return c + n;
      }).join("");
      const rows = flags.map((f) => {
         const ft = f.features || {};
         return `<tr>
            <td>${mmss(f.first_secs)}${f.last_secs > f.first_secs + 1 ? ` to ${mmss(f.last_secs)}` : ""}</td>
            <td>${esc(f.worksheet_name || "")}<div class="pr-email">${esc((ft.addresses || []).slice(0, 4).join(", "))}${(ft.addresses || []).length > 4 ? ", ..." : ""}</div></td>
            <td class="pr-num-cell">${f.novel_cells}<div class="pr-email">in ${f.events} change${f.events === 1 ? "" : "s"}</div></td>
            <td>${(f.reasons || []).map((r) => esc(AI_REASON[r] || r)).join("; ")}
               <div class="pr-email">${ft.max_idle_secs != null ? `out of the grid ${esc(ft.max_idle_secs)} s` : ""}${(ft.actions || []).length ? `; ${esc(ft.actions.join(", "))}` : ""}</div></td>
            <td class="ex-ai-sample">${(ft.sample || []).slice(0, 4).map((s) => `<code>${esc(s)}</code>`).join("<br>")}</td>
         </tr>`;
      }).join("");
      return `
         <div class="ex-ai-head">
            <span><strong>${esc(who ? who.name : `Student ${d.run_id}`)}</strong>: ${flags.length} AI flag${flags.length === 1 ? "" : "s"},
            ${series.reduce((a, x) => a + x.novel, 0)} new formulas, ${series.reduce((a, x) => a + x.copied, 0)} copied cells</span>
            <span class="ex-ai-btns"><button type="button" class="ghost" id="ex-ai-refresh">Refresh</button>
            <button type="button" class="ghost" id="ex-ai-close">Close</button></span>
         </div>
         <svg class="ex-ai-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" role="img" aria-label="Formula timeline">${bands}${bars}</svg>
         <div class="ex-ai-axis"><span>0:00</span><span>${mmss(end / 2)}</span><span>${mmss(end)}</span></div>
         <p class="ex-ai-legend"><span class="sw ex-ai-new"></span> new formulas <span class="sw ex-ai-off"></span> new, away from the cursor
            <span class="sw ex-ai-copy"></span> copied <span class="sw ex-ai-band"></span> flagged</p>
         ${flags.length ? `<div class="pr-scroll"><table class="cd-table ex-ai-table">
            <thead><tr><th>When</th><th>Where</th><th>New formulas</th><th>Why</th><th>Sample</th></tr></thead>
            <tbody>${rows}</tbody></table></div>` : `<p>No flags for this student.</p>`}`;
   }

   renderExam() {
      this.renderStatus();
      const el = this.querySelector("#ex-board");
      const d = this.exam;
      if (!d) return;
      const proj = this.querySelector("#ex-projector").checked;
      this.renderAiBar(proj);   // AI fingerprints (2026-10-17)
      if (d.self_start) {
         // SELF-START practice board (2026-10-19): each student's tries, best score, last try.
         const tried = d.students.filter((r) => r.attempts > 0).length;
         el.innerHTML = `
            <p class="ex-summary">${tried} of ${d.students.length} students have tried it.</p>
            <table class="pr-table"><thead><tr><th>Student</th><th class="pr-num-cell">Tries</th><th class="pr-num-cell">Best score</th><th>Last try</th>${proj ? "" : `<th class="pr-num-cell">AI flags</th>`}</tr></thead>
            <tbody>${d.students.map((r) => `<tr>
               <td>${esc(proj ? r.label : r.name)}${proj ? "" : `<div class="pr-email">${esc(r.email)}</div>`}</td>
               <td class="pr-num-cell">${r.attempts || ""}${r.working ? " (working now)" : ""}</td>
               <td class="pr-num-cell">${r.best_score != null ? `${r.best_score}%` : "-"}</td>
               <td>${r.last_at ? esc(new Date(r.last_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })) : "-"}</td>
               ${proj ? "" : `<td class="pr-num-cell ${r.ai_flags ? "ex-flag" : ""}">${r.ai_flags || ""}</td>`}
            </tr>`).join("")}</tbody></table>
            <p class="pr-foot">Best score = points correct over every tab, from the student's best finished try. A try still under way counts once it ends.</p>`;
         return;
      }
      const tabs = (d.pack.tabs || []);
      const STATUS = { not_joined: "Not in yet", working: "Working", finished: "All correct", time_up: "Time up" };
      let rows = d.students.slice();
      if (proj) rows.sort((a, b) => (b.points_correct ?? -1) - (a.points_correct ?? -1));
      const joined = rows.filter((r) => r.status !== "not_joined").length;
      el.innerHTML = `
         <p class="ex-summary">${joined} of ${rows.length} students in.</p>
         <div class="pr-scroll"><table class="cd-table ex-board">
            <thead><tr><th>${proj ? "#" : "Student"}</th>${proj ? "<th>Student</th>" : "<th>Status</th>"}<th>Score</th>
               ${tabs.map((t, i) => `<th title="${esc(t)}">Tab ${i + 1}</th>`).join("")}${proj ? "" : `${d.session && d.session.collect_file ? "<th>File</th>" : ""}<th>AI flags</th>`}</tr></thead>
            <tbody>
               ${rows.map((r, i) => `
                  <tr class="${r.status === "not_joined" ? "ex-out" : ""}">
                     <td>${proj ? i + 1 : `${esc(r.name)}<div class="pr-email">${esc(r.email)}${r.time_factor > 1 ? ` · extra time x${r.time_factor}` : ""}</div>`}</td>
                     <td>${proj ? esc(r.label) : esc(STATUS[r.status] || r.status)}</td>
                     <td class="pr-num-cell">${r.score == null ? "-" : `${r.score}%<div class="pr-email">${r.points_correct}/${r.points_total} pts</div>`}</td>
                     ${r.tabs.map((t) => `<td class="pr-num-cell ${!t.delivered ? "ex-dim" : t.correct === t.total && t.total ? "ex-full" : ""}">${!t.delivered ? "-" : `${t.correct ?? 0}/${t.total}`}</td>`).join("")}
                     ${proj ? "" : `${d.session && d.session.collect_file ? `<td>${r.file && r.run_id
                        ? `<button type="button" class="btn-mini ex-file" data-run="${r.run_id}" data-name="${esc(r.name)}">Download</button><div class="pr-email">${new Date(r.file.uploaded_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</div>`
                        : "-"}</td>` : ""}<td class="pr-num-cell ${r.ai_flags ? "ex-flag" : ""}">${r.run_id == null ? "" : r.ai_flags
                        ? `<button type="button" class="ex-flag-chip" data-ai-user="${esc(r.user_id)}" title="Show this student's AI flags">${r.ai_flags}</button>`
                        : `<button type="button" class="ex-flag-none" data-ai-user="${esc(r.user_id)}" title="Show this student's formula timeline">0</button>`}</td>`}
                  </tr>`).join("")}
            </tbody>
         </table></div>
         <p class="pr-foot">Each tab shows answers correct out of answers on that tab, from the student's last check (the add-in checks a tab
            about a second after every edit). Score = points correct over every tab of the exam, released or not.
            The clock is the same for everyone except students with extra time; a student who arrives late gets the time that is left.
            ${d.session && d.session.collect_file ? "Files: each student's workbook arrives when their exam ends (or when they press Submit my file); the latest copy is kept." : ""}</p>`;
      el.querySelectorAll(".ex-file").forEach((b) => b.addEventListener("click", () => this.downloadExamFile(b.dataset.run, b.dataset.name)));
   }

   /** One collected workbook (COLLECTED FILES, 2026-10-16). */
   async downloadExamFile(runId, name) {
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/packs/${encodeURIComponent(this.packId)}/exam/files/${encodeURIComponent(runId)}`);
         if (!res.ok) throw new Error(String(res.status));
         const cd = res.headers.get("Content-Disposition") || "";
         const m = cd.match(/filename="([^"]+)"/);
         const blob = await res.blob();
         const href = URL.createObjectURL(blob);
         const a = document.createElement("a");
         a.href = href;
         a.download = m ? m[1] : `${name || "student"}.xlsx`;
         document.body.appendChild(a);
         a.click();
         a.remove();
         setTimeout(() => URL.revokeObjectURL(href), 60000);
      } catch {
         alert("Couldn't download that file.");
      }
   }

   /* The class page's own read: works for a TA too, who cannot open the rules. */
   async loadHeader() {
      const title = this.querySelector("#pr-title");
      const sub = this.querySelector("#pr-sub");
      title.textContent = "Loading...";
      sub.textContent = "";
      this.cls = null;
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         this.cls = data.class;
         const a = (data.assignments || []).find((x) => Number(x.assignment_id) === this.packId);
         title.textContent = a ? a.title : "Timed pack";
         sub.textContent = `${data.class.course_code} · ${data.class.section} · timed exercise pack`;
         this.querySelector("#pr-back").textContent = `← ${data.class.course_code} · ${data.class.section}`;
         if (!a) sub.textContent += " (not attached to this class)";
      } catch {
         title.textContent = "Couldn't load this class.";
      }
   }

   /* ------------------------------------------------------------------ rules */

   async loadRules(keepDraft) {
      const form = this.querySelector("#pr-form");
      const msg = this.querySelector("#pr-msg");
      msg.textContent = "";
      msg.className = "msg";
      if (keepDraft && this.view) return;
      form.innerHTML = `<p class="empty">Loading...</p>`;
      this.querySelector("#pr-actions").hidden = true;
      const want = { classId: this.classId, packId: this.packId };
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/packs/${encodeURIComponent(this.packId)}/rules`);
         const data = await res.json();
         // A slow answer for a pack the teacher has since navigated away from must not
         // render (and then be saved) under the new pack's heading.
         if (this.classId !== want.classId || this.packId !== want.packId) return;
         if (!res.ok || !data.ok) throw new Error(data.reason || String(res.status));
         this.view = data;
         this.renderRules();
      } catch (err) {
         if (this.classId !== want.classId || this.packId !== want.packId) return;
         this.view = null;
         form.innerHTML = "";
         const why = String(err.message);
         msg.textContent =
            why === "not_authorized" || why === "403"
               ? "Only the class's instructor can see or change these rules. The best-time board is open to you."
               : why === "pack_not_in_class"
                  ? "This pack is not attached to this class."
                  : "Couldn't load the rules.";
         msg.className = "msg err";
      }
   }

   renderRules() {
      const v = this.view;
      const packRules = { ...v.defaults, ...(v.pack_rules || {}) };
      const override = v.override || {};
      const tabs = (v.pack.tabs || []).map((t) => t.sheet_name);

      this.querySelector("#pr-form").innerHTML = `
         <p class="pr-tabs-line">${tabs.length} tab${tabs.length === 1 ? "" : "s"}, delivered one at a time: ${tabs.map(esc).join(", ")}</p>
         <table class="cd-table pr-table">
            <thead><tr><th>Setting</th><th>Pack default</th><th>This class</th></tr></thead>
            <tbody>
               ${FIELDS.map((f) => {
                  const own = Object.prototype.hasOwnProperty.call(override, f.key);
                  return `
                  <tr data-key="${f.key}">
                     <td class="pr-label"><strong>${esc(f.label)}</strong><em>${esc(f.help)}</em></td>
                     <td class="pr-default">${describe(f, packRules[f.key])}</td>
                     <td class="pr-class">
                        <label class="pr-own"><input type="checkbox" class="pr-own-box" ${own ? "checked" : ""}> Set for this class</label>
                        <div class="pr-editor" ${own ? "" : "hidden"}>${this.editor(f, own ? override[f.key] : packRules[f.key])}</div>
                        <div class="pr-same" ${own ? "hidden" : ""}>Same as the pack</div>
                     </td>
                  </tr>`;
               }).join("")}
            </tbody>
         </table>`;

      const form = this.querySelector("#pr-form");
      form.querySelectorAll("tr[data-key]").forEach((tr) => {
         const box = tr.querySelector(".pr-own-box");
         box.addEventListener("change", () => {
            tr.querySelector(".pr-editor").hidden = !box.checked;
            tr.querySelector(".pr-same").hidden = box.checked;
            this.dirty();
         });
         tr.addEventListener("input", () => this.dirty());
         tr.addEventListener("click", (e) => {
            const b = e.target.closest("button[data-act]");
            if (!b) return;
            const body = tr.querySelector(".pr-rows tbody");
            if (b.dataset.act === "add") {
               const f = FIELDS.find((x) => x.key === tr.dataset.key);
               body.insertAdjacentHTML("beforeend", this.rowHtml(f, ["", ""]));
            } else if (b.dataset.act === "del") {
               b.closest("tr").remove();
            }
            this.dirty();
         });
      });
      this.querySelector("#pr-actions").hidden = false;
      this.querySelector("#pr-reset").disabled = !v.override;
      this.setSaveMsg("");
   }

   rowHtml(f, [limit, grade]) {
      const isTime = f.kind === "time-rows";
      const limitText = limit === null ? "" : limit === "" ? "" : isTime ? fmtClock(limit) : String(limit);
      const gradeText = grade === "" ? "" : String(Math.round(Number(grade) * 10000) / 100);
      return `
         <tr>
            <td>${isTime
               ? `<input class="pr-limit" inputmode="decimal" placeholder="m:ss" value="${esc(limitText)}" aria-label="Time">`
               : `<input class="pr-limit" inputmode="numeric" placeholder="runs" value="${esc(limitText)}" aria-label="Runs">`}</td>
            <td><input class="pr-grade" inputmode="decimal" value="${esc(gradeText)}" aria-label="Grade percent"><span class="pr-unit">%</span></td>
            <td><button type="button" class="pr-icon" data-act="del" aria-label="Remove row" title="Remove row">&times;</button></td>
         </tr>`;
   }

   editor(f, value) {
      switch (f.kind) {
         case "time-rows": {
            const rows = (value || []).filter(([t]) => t !== null);
            const longer = (value || []).find(([t]) => t === null);
            return `
               <table class="pr-rows">
                  <thead><tr><th>Time (m:ss) or faster</th><th>Grade</th><th></th></tr></thead>
                  <tbody>${rows.map((r) => this.rowHtml(f, r)).join("")}</tbody>
               </table>
               <button type="button" class="pr-add" data-act="add">+ Add a row</button>
               <label class="pr-longer"><input type="checkbox" class="pr-longer-box" ${longer ? "checked" : ""}>
                  Any finished run earns at least
                  <input class="pr-longer-grade" inputmode="decimal" value="${longer ? Math.round(longer[1] * 10000) / 100 : 50}" aria-label="Grade for any finished run">%</label>`;
         }
         case "attempt-rows":
            return `
               <table class="pr-rows">
                  <thead><tr><th>Completed runs, at least</th><th>Grade</th><th></th></tr></thead>
                  <tbody>${(value || []).map((r) => this.rowHtml(f, r)).join("")}</tbody>
               </table>
               <button type="button" class="pr-add" data-act="add">+ Add a row</button>`;
         case "departure":
            return `
               <select class="pr-input">
                  ${Object.entries(DEPARTURE).map(([k, d]) => `<option value="${k}" ${k === value ? "selected" : ""}>${esc(d.label)}</option>`).join("")}
               </select>
               <p class="pr-hint">${esc(DEPARTURE[value]?.help || "")}</p>`;
         case "secs":
            return `<input class="pr-input pr-num" inputmode="numeric" value="${esc(value)}"> <span class="pr-unit">seconds</span>`;
         case "minutes":
            return `<input class="pr-input pr-num" inputmode="decimal" value="${esc(Math.round((value / 60) * 10) / 10)}"> <span class="pr-unit">minutes</span>`;
         case "bool":
            return `
               <select class="pr-input">
                  <option value="true" ${value ? "selected" : ""}>On</option>
                  <option value="false" ${value ? "" : "selected"}>Off</option>
               </select>`;
         case "mode":
            return `
               <select class="pr-input">
                  <option value="timed" ${value !== "exam" ? "selected" : ""}>Timed pack</option>
                  <option value="exam" ${value === "exam" ? "selected" : ""}>Exam</option>
               </select>`;
         case "waves":
            return `<input class="pr-input" placeholder="3, 3" value="${esc(Array.isArray(value) ? value.join(", ") : "")}"> <span class="pr-unit">tabs per wave</span>`;
         case "opt-minutes":
            return `<input class="pr-input pr-num" inputmode="decimal" placeholder="blank = button only" value="${value == null ? "" : esc(Math.round((value / 60) * 10) / 10)}"> <span class="pr-unit">minutes</span>`;
         case "int":
            return `<input class="pr-input pr-num" inputmode="numeric" value="${esc(value)}"> <span class="pr-unit">${esc(f.unit || "")}</span>`;
         default:
            return "";
      }
   }

   /*
      Read one setting's editor back into a rule value. Returns { value } or
      { error }. Threshold rows are SORTED here (time up, runs down), because the
      grade is the first row that matches and a teacher should not have to type
      them in order. Grades that rise as the time gets slower are NOT re-sorted:
      that is a mistake to point out, not to guess at.
   */
   read(f, tr) {
      const num = (s) => (String(s).trim() === "" ? NaN : Number(String(s).trim()));
      switch (f.kind) {
         case "time-rows":
         case "attempt-rows": {
            const isTime = f.kind === "time-rows";
            const rows = [];
            for (const r of tr.querySelectorAll(".pr-rows tbody tr")) {
               const lt = r.querySelector(".pr-limit").value;
               const gt = r.querySelector(".pr-grade").value;
               if (!String(lt).trim() && !String(gt).trim()) continue;          // blank row: ignore
               const limit = isTime ? parseClock(lt) : num(lt);
               const g = num(gt);
               if (isTime && !(limit > 0)) return { error: `"${lt}" is not a time. Use m:ss, like 2:55.` };
               if (!isTime && !(Number.isInteger(limit) && limit >= 1)) return { error: `"${lt}" is not a whole number of runs (1 or more).` };
               if (!(g >= 0 && g <= 100)) return { error: `"${gt}" is not a grade between 0 and 100.` };
               rows.push([isTime ? Math.round(limit * 10) / 10 : limit, Math.round(g * 100) / 10000]);
            }
            rows.sort((a, b) => (isTime ? a[0] - b[0] : b[0] - a[0]));
            for (let i = 1; i < rows.length; i++) {
               if (rows[i][0] === rows[i - 1][0]) return { error: `Two rows say ${isTime ? fmtClock(rows[i][0]) : rows[i][0]}.` };
               if (rows[i][1] > rows[i - 1][1]) {
                  return { error: isTime
                     ? `${fmtClock(rows[i][0])} earns more than ${fmtClock(rows[i - 1][0])}. A slower time cannot earn a higher grade.`
                     : `${rows[i][0]} runs earn more than ${rows[i - 1][0]}. Fewer runs cannot earn a higher grade.` };
               }
            }
            if (isTime && tr.querySelector(".pr-longer-box").checked) {
               const lg = num(tr.querySelector(".pr-longer-grade").value);
               if (!(lg >= 0 && lg <= 100)) return { error: "The grade for any finished run must be between 0 and 100." };
               const last = rows.length ? rows[rows.length - 1][1] : 1;
               if (lg / 100 > last) return { error: "Any finished run cannot earn more than the slowest time row." };
               rows.push([null, Math.round(lg * 100) / 10000]);
            }
            return { value: rows };
         }
         case "departure":
            return { value: tr.querySelector(".pr-input").value };
         case "secs": {
            const n = num(tr.querySelector(".pr-input").value);
            if (!(Number.isInteger(n) && n >= 0 && n <= 3600)) return { error: "Penalty must be a whole number of seconds, 0 to 3600." };
            return { value: n };
         }
         case "minutes": {
            const n = num(tr.querySelector(".pr-input").value);
            const secs = Math.round(n * 60);
            if (!(secs >= 60 && secs <= 36000)) return { error: "The time limit must be between 1 and 600 minutes." };
            return { value: secs };
         }
         case "bool":
            return { value: tr.querySelector(".pr-input").value === "true" };
         case "mode":
            return { value: tr.querySelector(".pr-input").value === "exam" ? "exam" : "timed" };
         case "waves": {
            const t = String(tr.querySelector(".pr-input").value).trim();
            if (!t) return { value: null };
            const w = t.split(/[,\s]+/).filter(Boolean).map(Number);
            if (!(w.length >= 1 && w.length <= 2 && w.every((n) => Number.isInteger(n) && n >= 1)))
               return { error: "Type one or two whole numbers of tabs, like 3, 3." };
            return { value: w };
         }
         case "opt-minutes": {
            const t = String(tr.querySelector(".pr-input").value).trim();
            if (!t) return { value: null };
            const secs = Math.round(Number(t) * 60);
            if (!(Number.isFinite(secs) && secs >= 0 && secs <= 36000)) return { error: "Minutes must be between 0 and 600, or blank." };
            return { value: secs };
         }
         case "int": {
            const n = num(tr.querySelector(".pr-input").value);
            if (!(Number.isInteger(n) && n >= f.min && n <= f.max)) return { error: `Use a whole number from ${f.min} to ${f.max}.` };
            return { value: n };
         }
         default:
            return { error: "unknown setting" };
      }
   }

   collect() {
      const override = {};
      const errors = [];
      for (const tr of this.querySelectorAll("#pr-form tr[data-key]")) {
         if (!tr.querySelector(".pr-own-box").checked) continue;
         const f = FIELDS.find((x) => x.key === tr.dataset.key);
         const r = this.read(f, tr);
         if (r.error) errors.push(`${f.label}: ${r.error}`);
         else override[f.key] = r.value;
      }
      return { override, errors };
   }

   dirty() {
      // keep the departure hint in step with the chosen option
      const dep = this.querySelector('#pr-form tr[data-key="departure"]');
      if (dep) {
         const sel = dep.querySelector(".pr-input");
         const hint = dep.querySelector(".pr-hint");
         if (sel && hint) hint.textContent = DEPARTURE[sel.value]?.help || "";
      }
      const { override, errors } = this.collect();
      const changed = errors.length > 0 || !same(override, this.view?.override || {});
      this.setSaveMsg(changed ? "Unsaved changes" : "");
   }

   setSaveMsg(text, kind = "") {
      const el = this.querySelector("#pr-save-msg");
      el.textContent = text;
      el.className = `msg${kind ? " " + kind : ""}`;
   }

   async save() {
      const { override, errors } = this.collect();
      if (errors.length) {
         this.setSaveMsg(errors.join(" "), "err");
         return;
      }
      await this.put(Object.keys(override).length ? override : null);
   }

   async reset() {
      await this.put(null);
   }

   async put(override) {
      const btns = this.querySelectorAll("#pr-actions button");
      btns.forEach((b) => (b.disabled = true));
      this.setSaveMsg("Saving...");
      try {
         const res = await api(`/classes/${encodeURIComponent(this.classId)}/packs/${encodeURIComponent(this.packId)}/rules`, {
            method: "PUT",
            body: JSON.stringify({ override }),
         });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            if (data.reason === "invalid_rules") {
               this.setSaveMsg(`Not saved: ${(data.errors || []).join("; ")}`, "err");
               return;
            }
            throw new Error(data.reason || String(res.status));
         }
         this.view = null;
         await this.loadRules(false);
         const n = Number(data.regraded || 0);
         const moved = n ? ` Grades for ${n} student${n === 1 ? "" : "s"} recalculated, gradebook included.` : "";
         this.setSaveMsg((override
            ? "Saved. The other settings apply to runs started from now on."
            : "Saved. This class now uses the pack's own rules.") + moved, "ok");
      } catch (err) {
         this.setSaveMsg(`Save failed (${err.message}).`, "err");
      } finally {
         btns.forEach((b) => (b.disabled = false));
         this.querySelector("#pr-reset").disabled = !this.view?.override;
      }
   }

   /* ------------------------------------------------------------------ board */

   async loadBoard() {
      const el = this.querySelector("#pr-board");
      const msg = this.querySelector("#pr-board-msg");
      msg.textContent = "";
      msg.className = "msg";
      el.innerHTML = `<p class="empty">Loading...</p>`;
      const want = { classId: this.classId, packId: this.packId };
      try {
         const res = await api(`/packs/${encodeURIComponent(this.packId)}/board?class_id=${encodeURIComponent(this.classId)}`);
         const data = await res.json();
         if (this.classId !== want.classId || this.packId !== want.packId) return;
         if (!res.ok || !data.ok) throw new Error(data.reason || String(res.status));
         this.renderBoard(data.board || []);
      } catch {
         el.innerHTML = "";
         msg.textContent = "Couldn't load the board.";
         msg.className = "msg err";
      }
   }

   renderBoard(rows) {
      const el = this.querySelector("#pr-board");
      const open = this.cls ? !!this.cls.students_see_scoreboard : null;
      const foot = `
         <p class="pr-foot">
            Completed timed runs only: practice, stopped, expired and restarted runs are not on the board.
            Times include penalty seconds. They leave out the seconds between a tab being all correct and the
            next tab arriving (the extra check and the delivery), up to 15 seconds per tab.
            ${open === null ? "" : open
               ? "Students in this class can see this board on <em>Class standings</em>, by nickname, without the grade column."
               : `Students cannot see this board. Turn on <em>Class scoreboard</em> on the <a href="#class-detail/${this.classId}">class page</a> to show it to them, by nickname, without the grade column.`}
         </p>`;
      if (!rows.length) {
         el.innerHTML = `<p class="empty">Nobody in this class has finished a timed run yet.</p>${foot}`;
         return;
      }
      el.innerHTML = `
         <div class="pr-scroll"><table class="cd-table pr-board">
            <thead>
               <tr><th>#</th><th>Student</th><th>Classmates see</th><th>Best time</th><th>Penalty in best</th>
                   <th>Finished runs</th><th>Grade</th><th>Best run</th></tr>
            </thead>
            <tbody>
               ${rows.map((r) => `
                  <tr>
                     <td class="pr-rank">${r.rank}</td>
                     <td>${esc(r.name || "")}${r.email ? `<div class="pr-email">${esc(r.email)}</div>` : ""}</td>
                     <td>${esc(r.nickname)}</td>
                     <td class="pr-num-cell">${fmtClock(r.best_total_secs)}${r.best_paused_secs >= 0.1 ? `<div class="pr-email">${Number(r.best_paused_secs).toFixed(1)} s of checking not counted</div>` : ""}</td>
                     <td class="pr-num-cell">${r.best_penalty_secs ? `${r.best_penalty_secs} s (${r.best_departures} departure${r.best_departures === 1 ? "" : "s"})` : "-"}</td>
                     <td class="pr-num-cell">${r.completed}</td>
                     <td class="pr-num-cell">${r.grade == null ? "-" : pct(r.grade)}</td>
                     <td class="pr-when">${r.best_at ? new Date(r.best_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</td>
                  </tr>`).join("")}
            </tbody>
         </table></div>${foot}`;
   }
}

customElements.define("app-pack-rules", AppPackRules);
